import { useMemo } from 'react'
import { MobileAction, MobileEventFlow, MOBILE_EVENT_CODE } from '@mobileTypes'
import { MOBILE_EVENT_CONTEXTS, EventContextParam } from '@mobileConstants'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

/** Descriptor of the element to filter event flows by. */
export type EventFlowElementRef =
  | { type: 'drawer_item'; drawerItemInternalId: string; drawerItemIndex?: number }
  | { type: 'button'; buttonId: string }
  | { type: 'swipe'; buttonId: string }
  | { type: 'field'; fieldId: string }
  | { type: 'embedded_list'; elementId: string }

export function isFlowForElement(flow: MobileEventFlow, ref: EventFlowElementRef): boolean {
  if (ref.type === 'drawer_item') {
    if (ref.drawerItemInternalId && flow.drawerItemInternalId) {
      return (
        flow.eventCode === MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS &&
        flow.drawerItemInternalId === ref.drawerItemInternalId
      )
    }
    if (ref.drawerItemIndex != null && flow.drawerItemIndex != null) {
      return (
        flow.eventCode === MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS &&
        flow.drawerItemIndex === ref.drawerItemIndex
      )
    }
    return false
  }
  if (ref.type === 'button') {
    return (
      flow.eventCode === MOBILE_EVENT_CODE.ON_BUTTON_PRESS && flow.buttonId === ref.buttonId
    )
  }
  if (ref.type === 'swipe') {
    return (
      flow.eventCode === MOBILE_EVENT_CODE.ON_BUTTON_SWIPE && flow.buttonId === ref.buttonId
    )
  }
  if (ref.type === 'field') {
    return (
      flow.eventCode === MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE && flow.fieldId === ref.fieldId
    )
  }
  if (ref.type === 'embedded_list') {
    return (
      (flow.eventCode === MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE ||
        flow.eventCode === MOBILE_EVENT_CODE.ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU) &&
      flow.elementId === ref.elementId
    )
  }
  return false
}

export interface EventFlowsForElementViewProps {
  eventFlows: MobileEventFlow[]
  elementRef: EventFlowElementRef
  /** Optional: drawer items for display labels (when element is drawer_item) */
  drawerItems?: { internalId?: string; label?: string }[]
}

function ActionChipContent({ action }: { action: MobileAction }) {
  const meta = action.meta ?? (action as any)
  return (
    <Box>
      <Typography variant='caption' display='block'>
        {action.actionCode}
      </Typography>
      {(meta.command || (action as any).command) && (
        <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
          📦 {(meta.command || (action as any).command).objectCode}:
          {(meta.command || (action as any).command).name}
        </Typography>
      )}
      {(meta.screenName || (action as any).screenName) && (
        <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
          → {meta.screenName || (action as any).screenName}
          {(meta.navigationParams || (action as any).navigationParams) &&
            ` (${meta.navigationParams || (action as any).navigationParams})`}
        </Typography>
      )}
      {(meta.variableName || (action as any).variableName) && (
        <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
          🔧 {meta.variableName || (action as any).variableName}
        </Typography>
      )}
      {meta.intervalId && (
        <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
          ⏱ {meta.intervalId}
          {meta.intervalMs && ` (${meta.intervalMs}ms)`}
        </Typography>
      )}
      {action.dependsOn && action.dependsOn.length > 0 && (
        <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
          depends: {action.dependsOn.join(', ')}
        </Typography>
      )}
    </Box>
  )
}

export const EventFlowsForElementView = ({
  eventFlows,
  elementRef,
  drawerItems = [],
}: EventFlowsForElementViewProps) => {
  const matchingFlows = useMemo(
    () => eventFlows.filter(flow => isFlowForElement(flow, elementRef)),
    [eventFlows, elementRef]
  )

  if (matchingFlows.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
        <Typography variant='body2' color='text.secondary'>
          No event flows use this element.
        </Typography>
      </Paper>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant='subtitle2' color='text.secondary'>
        {matchingFlows.length} event flow{matchingFlows.length !== 1 ? 's' : ''} for this element
      </Typography>
      {matchingFlows.map((flow: MobileEventFlow) => (
        <Card key={flow.id} variant='outlined' sx={{ bgcolor: 'grey.50' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Event: {flow.eventCode}
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
              Flow ID: {flow.id}
            </Typography>
            {!!MOBILE_EVENT_CONTEXTS[flow.eventCode]?.availableParams?.length && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                  px: 1,
                  py: 0.5,
                  bgcolor: 'primary.50',
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'primary.main',
                }}
              >
                <Typography variant='caption' fontWeight='bold' color='primary.main'>
                  Context:
                </Typography>
                {MOBILE_EVENT_CONTEXTS[flow.eventCode].availableParams.map(
                  (param: EventContextParam) => (
                    <Chip
                      key={param.name}
                      label={`${param.name}: ${param.type}`}
                      size='small'
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'primary.main', color: 'white' }}
                    />
                  )
                )}
              </Box>
            )}
            {flow.actions.length === 0 ? (
              <Typography variant='body2' color='text.secondary' sx={{ ml: 0 }}>
                No actions
              </Typography>
            ) : (
              <Stack direction='row' flexWrap='wrap' sx={{ gap: 1 }}>
                {flow.actions.map((action: MobileAction, actionIndex: number) => {
                  const hasNoDependencies = !action.dependsOn || action.dependsOn.length === 0
                  return (
                    <Box key={action.id} sx={{ display: 'flex', alignItems: 'center' }}>
                      {actionIndex > 0 && !hasNoDependencies && (
                        <ArrowForwardIcon fontSize='small' sx={{ mx: 0.5, color: 'text.secondary' }} />
                      )}
                      <Chip
                        label={<ActionChipContent action={action} />}
                        size='small'
                        sx={{ height: 'auto', py: 0.5 }}
                        variant='outlined'
                      />
                    </Box>
                  )
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}
